/**
 * @license
 * Copyright 2026 okayest! studios LLC. All Rights Reserved.
 */

import { LitElement, css, html } from 'lit';
import { customElement, query, state } from 'lit/decorators.js';
import WaDialog from '@awesome.me/webawesome/dist/components/dialog/dialog.js';
import WaInput from '@awesome.me/webawesome/dist/components/input/input.js';
import WaSelect from '@awesome.me/webawesome/dist/components/select/select.js';
import WaSwitch from '@awesome.me/webawesome/dist/components/switch/switch.js';

import '@awesome.me/webawesome/dist/components/button/button.js';
import '@awesome.me/webawesome/dist/components/dialog/dialog.js';
import '@awesome.me/webawesome/dist/components/icon/icon.js';
import '@awesome.me/webawesome/dist/components/input/input.js';
import '@awesome.me/webawesome/dist/components/option/option.js';
import '@awesome.me/webawesome/dist/components/select/select.js';
import '@awesome.me/webawesome/dist/components/switch/switch.js';
import { showAlert, showConfirmation } from '../../components/confirm-dialog';
import { RowCounter, calculateCount, calculateRepeats, getCounter, getSubcounterCount, getSubcounters } from '../data/counter-model';
import { addCounter, deleteCounter, getCounterState, updateCounter } from '../data/counter-store';

const defaultRowsInRepeat = 4;
const nameMaxLength = 512;

// Dialog for creating a new row counter or editing an existing one.
@customElement('yl-counter-edit-dialog')
export class CounterEditDialog extends LitElement {
  static styles = css`
    wa-dialog {
      --width: 30rem;
    }

    form {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    .counts {
      display: flex;
      gap: 16px;
    }

    .counts wa-input {
      width: 110px;
    }

    .note {
      margin: -8px 4px 0;
      font-size: var(--wa-font-size-s);
      color: var(--yl-secondary-text);
    }

    .option-card {
      border-radius: var(--wa-border-radius-l);
      background: var(--wa-color-surface-lowered);
    }

    .option-card > * {
      padding: 12px 16px;
    }

    .option-card > * + * {
      border-top: 1px solid var(--wa-color-surface-border);
    }

    .option-title {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .option-title wa-icon {
      width: 20px;
      color: var(--yl-secondary-text);
    }

    wa-switch {
      width: 100%;
    }

    wa-switch::part(switch) {
      flex-direction: row-reverse;
      justify-content: space-between;
    }

    .reset-every {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 8px;
    }

    .reset-every wa-input {
      width: 80px;
    }

    .reset-every wa-input::part(form-control-label) {
      position: absolute;
      width: 1px;
      height: 1px;
      overflow: hidden;
      clip-path: inset(50%);
      white-space: nowrap;
    }

    .description {
      margin: 8px 0 0;
      font-size: var(--wa-font-size-s);
      color: var(--yl-secondary-text);
    }

    .start-at {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .start-at span {
      flex: 1;
    }

    .start-at wa-select {
      width: 80px;
    }

    .delete {
      align-self: flex-start;
    }
  `;

  @query('wa-dialog')
  private _dialog!: WaDialog;

  @query('wa-input[name="name"]')
  private _nameInput!: WaInput;

  @state()
  private _counterId: string | null = null;

  @state()
  private _name: string = '';

  @state()
  private _rowsText: string = '';

  @state()
  private _repeatsText: string = '';

  @state()
  private _repeating: boolean = false;

  @state()
  private _rowsInRepeat: number = defaultRowsInRepeat;

  @state()
  private _rowsInRepeatText: string = '';

  @state()
  private _startAtOne: boolean = false;

  @state()
  private _parentId: string | null = null;

  @state()
  private _totalRows: number = 0;

  private _original: RowCounter | null = null;
  private _forceClose: boolean = false;

  /** Opens the dialog to edit [counterId], or to create a counter if null. */
  show(counterId: string | null) {
    const state = getCounterState();
    const counter = counterId !== null ? getCounter(state, counterId) : null;
    this._counterId = counter?.id ?? null;
    this._original = counter;
    this._name = counter?.label ?? '';
    this._repeating = counter !== null && counter.rowsInRepeat !== null;
    this._rowsInRepeat = counter?.rowsInRepeat ?? defaultRowsInRepeat;
    this._rowsInRepeatText = `${this._rowsInRepeat}`;
    this._startAtOne = counter?.startAtOne ?? false;
    this._totalRows = counter?.rows ?? (this._startAtOne ? 1 : 0);
    this._parentId = counter?.parentId ?? null;
    this._forceClose = false;
    this._updateRowRepeatInputs();
    this._dialog.open = true;
  }

  private get _isNew(): boolean {
    return this._counterId === null;
  }

  private get _startCount(): number {
    return this._startAtOne ? 1 : 0;
  }

  private get _changed(): boolean {
    const original = this._original;
    if (original === null) {
      return this._name.trim() !== '';
    }
    return (
      this._name !== original.label ||
      this._totalRows !== original.rows ||
      this._repeating !== (original.rowsInRepeat !== null) ||
      (this._repeating && this._rowsInRepeat !== original.rowsInRepeat) ||
      this._startAtOne !== original.startAtOne ||
      this._parentId !== original.parentId
    );
  }

  private _updateRowRepeatInputs() {
    const rowsInRepeat = this._repeating ? this._rowsInRepeat : null;
    this._rowsText = `${calculateCount(this._totalRows, this._startCount, rowsInRepeat)}`;
    this._repeatsText = `${calculateRepeats(this._totalRows, this._startCount, rowsInRepeat)}`;
  }

  private _updateTotalRows() {
    let total = parseCount(this._rowsText) ?? this._startCount;
    if (this._repeating) {
      total += (parseCount(this._repeatsText) ?? 0) * this._rowsInRepeat;
    }
    this._totalRows = total;
  }

  private _onCountInput(e: Event, field: '_rowsText' | '_repeatsText') {
    this[field] = digitsOnly(e.target as WaInput);
    this._updateTotalRows();
  }

  private _onRepeatingChange(e: Event) {
    this._repeating = (e.target as WaSwitch).checked;
    this._updateRowRepeatInputs();
  }

  private _onRowsInRepeatInput(e: Event) {
    this._rowsInRepeatText = digitsOnly(e.target as WaInput);
    const value = parseCount(this._rowsInRepeatText);
    if (value === null || value < 1) return;
    this._rowsInRepeat = value;
    this._updateRowRepeatInputs();
  }

  private _onLinkedChange(topLevelCounters: RowCounter[], e: Event) {
    const linked = (e.target as WaSwitch).checked;
    this._parentId = linked ? (topLevelCounters[0]?.id ?? null) : null;
  }

  private _onStartAtChange(e: Event) {
    this._startAtOne = (e.target as WaSelect).value === '1';
    if (this._startAtOne && this._totalRows === 0) {
      this._totalRows = 1;
    }
    this._updateRowRepeatInputs();
  }

  private async _onHide(e: CustomEvent<{ source: Element }>) {
    if (e.target !== this._dialog || this._forceClose || !this._changed) return;
    e.preventDefault();
    const discard = await showConfirmation({
      message: 'Discard unsaved changes?',
      confirmText: 'Discard',
      destructive: true,
    });
    if (discard) this._close();
  }

  private _close() {
    this._forceClose = true;
    this._dialog.open = false;
  }

  private async _save(e?: Event) {
    e?.preventDefault();
    if (!this._changed) return;

    const name = this._name.trim();
    this._nameInput.setCustomValidity(
      name === '' ? 'Please enter a name' : name.length > nameMaxLength ? 'Name is too long' : '',
    );
    if (!this._nameInput.reportValidity()) return;

    if (this._startAtOne && parseCount(this._rowsText) === 0) {
      await showAlert(
        'Row count must be 1 or greater. Change "Start count at" to "0" if you would like a zero-based counter.',
      );
      return;
    }

    const fields = {
      label: name,
      rows: this._totalRows,
      rowsInRepeat: this._repeating ? this._rowsInRepeat : null,
      parentId: this._parentId,
      startAtOne: this._startAtOne,
    };
    if (this._counterId === null) {
      addCounter(fields);
    } else {
      updateCounter(this._counterId, fields);
    }
    this._close();
  }

  private async _delete() {
    const counterId = this._counterId;
    if (counterId === null) return;
    let message = 'Are you sure you want to delete this counter?';
    if ((this._original?.parentId ?? null) !== null) {
      message += ' Row count will be removed from linked counter.';
    }
    const confirmed = await showConfirmation({ message, confirmText: 'Delete', destructive: true });
    if (!confirmed) return;
    deleteCounter(counterId);
    this._close();
  }

  render() {
    const state = getCounterState();
    const counterId = this._counterId;
    const hasSubcounters = counterId !== null && getSubcounters(state, counterId).length > 0;
    const countFromSubcounters = counterId !== null ? getSubcounterCount(state, counterId) : 0;
    const topLevelCounters = state.counters.filter((c) => c.parentId === null && c.id !== counterId);

    return html`<wa-dialog
      label=${this._isNew ? 'New counter' : 'Edit counter'}
      @wa-hide=${this._onHide}
      @wa-after-show=${(e: Event) => e.target === this._dialog && this._isNew && this._nameInput.focus()}
    >
      <form @submit=${this._save} novalidate>
        <wa-input
          name="name"
          label="Name"
          size="l"
          maxlength=${nameMaxLength}
          autocapitalize="sentences"
          enterkeyhint="done"
          .value=${this._name}
          @input=${(e: Event) => {
            this._name = (e.target as WaInput).value ?? '';
            this._nameInput.setCustomValidity('');
          }}
        ></wa-input>

        <div class="counts">
          <wa-input
            label="Row count"
            inputmode="numeric"
            placeholder=${this._startCount}
            .value=${this._rowsText}
            @input=${(e: Event) => this._onCountInput(e, '_rowsText')}
          ></wa-input>
          ${this._repeating
            ? html`<wa-input
                label="Repeats"
                inputmode="numeric"
                placeholder="0"
                .value=${this._repeatsText}
                @input=${(e: Event) => this._onCountInput(e, '_repeatsText')}
              ></wa-input>`
            : null}
        </div>
        ${this._repeating && this._totalRows > this._rowsInRepeat
          ? html`<p class="note">${this._totalRows} total rows</p>`
          : null}
        ${countFromSubcounters > 0
          ? html`<p class="note">
              +${countFromSubcounters} from linked counters = ${this._totalRows + countFromSubcounters}
            </p>`
          : null}

        <div class="option-card">
          <wa-switch .checked=${this._repeating} hint="Reset at a certain row count" @change=${this._onRepeatingChange}>
            <span class="option-title"><wa-icon name="retweet"></wa-icon>Repeating</span>
          </wa-switch>
          ${this._repeating
            ? html`<div>
                <div class="reset-every">
                  Reset every
                  <wa-input
                    label="Rows in repeat"
                    inputmode="numeric"
                    placeholder=${defaultRowsInRepeat}
                    .value=${this._rowsInRepeatText}
                    @input=${this._onRowsInRepeatInput}
                    @blur=${() => (this._rowsInRepeatText = `${this._rowsInRepeat}`)}
                  ></wa-input>
                  rows
                </div>
                <p class="description">
                  ${this._startAtOne
                    ? `Count resets to 1 when row count passes ${this._rowsInRepeat}`
                    : `Count resets to 0 when row count reaches ${this._rowsInRepeat}`}
                </p>
              </div>`
            : null}
        </div>

        ${!hasSubcounters && topLevelCounters.length > 0
          ? html`<div class="option-card">
              <wa-switch
                .checked=${this._parentId !== null}
                hint="Roll up to parent counter"
                @change=${(e: Event) => this._onLinkedChange(topLevelCounters, e)}
              >
                <span class="option-title"><wa-icon name="link"></wa-icon>Linked counter</span>
              </wa-switch>
              ${this._parentId !== null
                ? html`<div>
                    <wa-select
                      label="Parent counter"
                      .value=${this._parentId}
                      @change=${(e: Event) => (this._parentId = (e.target as WaSelect).value as string)}
                    >
                      ${topLevelCounters.map(
                        (c) => html`<wa-option value=${c.id}>${c.label || 'Untitled counter'}</wa-option>`,
                      )}
                    </wa-select>
                  </div>`
                : null}
            </div>`
          : null}

        <div class="option-card">
          <div class="start-at">
            <wa-icon name="forward-step"></wa-icon>
            <span id="start-at-label">Start count at</span>
            <wa-select aria-labelledby="start-at-label" .value=${this._startAtOne ? '1' : '0'} @change=${this._onStartAtChange}>
              <wa-option value="0">0</wa-option>
              <wa-option value="1">1</wa-option>
            </wa-select>
          </div>
        </div>

        ${!this._isNew
          ? html`<wa-button class="delete" variant="danger" appearance="outlined" @click=${this._delete}>
              <wa-icon slot="start" name="trash"></wa-icon>
              Delete counter
            </wa-button>`
          : null}
        <button type="submit" hidden></button>
      </form>
      <wa-button slot="footer" appearance="plain" data-dialog="close">Cancel</wa-button>
      <wa-button slot="footer" variant="brand" ?disabled=${!this._changed} @click=${() => this._save()}>
        ${this._isNew ? 'Add' : 'Save'}
      </wa-button>
    </wa-dialog>`;
  }
}

/** Strips non-digit characters from [input], returning its new value. */
function digitsOnly(input: WaInput): string {
  const digits = (input.value ?? '').replace(/\D/g, '');
  if (digits !== input.value) input.value = digits;
  return digits;
}

/** Parses a non-negative integer, or null if blank or invalid. */
function parseCount(text: string): number | null {
  const trimmed = text.trim();
  return /^\d+$/.test(trimmed) ? parseInt(trimmed, 10) : null;
}

declare global {
  interface HTMLElementTagNameMap {
    'yl-counter-edit-dialog': CounterEditDialog;
  }
}
